import { PlainTextElement } from './text/PlainText/PlainTextElement';
import { ColumnsElement } from './columns/ColumnsElement';
import { GraphElement } from './graph/GraphElement';
import { ImageGalleryElement } from './imageGallery/ImageGalleryElement';
import { ImageElement } from './image/ImageElement';
import { PageElement } from './page/PageElement';
import { PageBreakElement } from './pageBreak/PageBreakElement';
import { RepeatElement } from './repeat/RepeatElement';
import { RowsElement } from './rows/RowsElement';
import { TableElement } from './table/TableElement';
import { TemplateIncludeElement } from './templateInclude/TemplateIncludeElement';
import { RichTextElement } from './text/RichText/RichTextElement';
import {
    createElementClassRegistry,
    getElementClassForType,
    getElementInsertionOptionsForSlotFromRegistry,
} from './elementInsertionOptions';
import { containsFlowableElement } from './elementLayout';
import { createGraphRuntimeManager } from './graphRuntime';
import type { Profile } from '../../../../profile';
import { createPageProfileRuntime } from './pageProfileRuntime';
import { appendStructurePath, formatStructurePath } from './structurePath';
import type { RuntimeDataServiceId } from '../runtime/dataServices/contracts';
import type {
    ActiveDataBinding,
    RuntimeBindingResolveContext,
} from '../runtime/dataServices/types';
import type { PageSettings } from '../pageSettings';
import type { ReportStructureController } from '../reportStructureController';
import type {
    CoreRenderRuntime,
    EditorRenderRuntime,
    ElementDefinition,
    ElementInsertionOption,
    ElementInsertionSlotKind,
    ElementLocation,
    PreviewRenderRuntime,
    RenderContextKind,
    RenderRuntime,
    RenderRuntimeDataServices,
    RenderRuntimeGraphSettings,
    RenderSurface,
    ReportElement,
    ReportElementConstructor,
} from './types';

export const reportElementClasses = [
    PageElement,
    PlainTextElement,
    RichTextElement,
    ImageElement,
    ImageGalleryElement,
    TableElement,
    TemplateIncludeElement,
    RepeatElement,
    PageBreakElement,
    RowsElement,
    ColumnsElement,
    GraphElement,
] as const satisfies readonly ReportElementConstructor[];

export function getElementInsertionOptionsForSlot(
    elementClasses: readonly ReportElementConstructor[],
    contextKind: RenderContextKind,
    slotKind: ElementInsertionSlotKind
): ElementInsertionOption[] {
    return getElementInsertionOptionsForSlotFromRegistry(
        contextKind,
        slotKind,
        createElementClassRegistry(elementClasses)
    );
}

function appendTemplateIncludeChainOrThrow(
    includeChain: readonly string[],
    templateId: string
): readonly string[] {
    if (includeChain.includes(templateId)) {
        const cyclePath = [...includeChain, templateId].join(' -> ');
        throw new Error(`Recursive template_include chain detected: ${cyclePath}.`);
    }

    return [...includeChain, templateId];
}

// Keep runtime services explicit and pass them through render calls.
export function createRenderRuntime(
    elementClasses: readonly ReportElementConstructor[],
    showEditor: true,
    pageSettings: PageSettings,
    graphSettings: RenderRuntimeGraphSettings | undefined,
    dataServices: RenderRuntimeDataServices,
    renderSurface: 'editor',
    profile: Profile,
    structureController: ReportStructureController
): EditorRenderRuntime;
export function createRenderRuntime(
    elementClasses: readonly ReportElementConstructor[],
    showEditor: false,
    pageSettings: PageSettings,
    graphSettings: RenderRuntimeGraphSettings | undefined,
    dataServices: RenderRuntimeDataServices,
    renderSurface: 'preview' | 'export',
    profile: Profile
): PreviewRenderRuntime;
export function createRenderRuntime(
    elementClasses: readonly ReportElementConstructor[],
    showEditor: boolean,
    pageSettings: PageSettings,
    graphSettings: RenderRuntimeGraphSettings | undefined,
    dataServices: RenderRuntimeDataServices,
    renderSurface: RenderSurface,
    profile: Profile,
    structureController?: ReportStructureController
): RenderRuntime {
    const elementClassRegistry = createElementClassRegistry(elementClasses);
    const graphRuntime = createGraphRuntimeManager(graphSettings, profile);
    const runtimeGraphResolveContext: RuntimeBindingResolveContext = graphSettings
        ? {
              graphSettings,
              graphTimeWindow: graphRuntime.getGraphTimeWindow(),
          }
        : {};
    const pageProfileRuntime = createPageProfileRuntime(pageSettings);
    let currentElementLocation: ElementLocation | null = null;

    function getCurrentElementLocation(): ElementLocation | null {
        return currentElementLocation;
    }

    function withElementLocation<T>(location: ElementLocation, render: () => T): T {
        const previousLocation = currentElementLocation;
        currentElementLocation = location;
        try {
            return render();
        } finally {
            currentElementLocation = previousLocation;
        }
    }

    function requireCurrentElementLocation(settingKey: string): ElementLocation {
        const location = getCurrentElementLocation();
        if (location) {
            return location;
        }

        throw new Error(
            `Missing current element location while resolving settings path "${settingKey}".`
        );
    }

    function getCurrentElementChildListPath(settingKey: string) {
        const location = requireCurrentElementLocation(settingKey);
        return appendStructurePath(
            appendStructurePath(location.elementPath, 'settings'),
            settingKey
        );
    }

    function getCurrentElementChildListItemPath(settingKey: string, index: number) {
        return appendStructurePath(getCurrentElementChildListPath(settingKey), index);
    }

    function getBindingGraphRuntimeState({
        context,
        graphSettings: bindingGraphSettings,
        graphTimeWindow,
    }: RuntimeBindingResolveContext) {
        if (context?.kind !== 'measuring-point') {
            throw new Error('Runtime binding DataSet requires a measuring-point context.');
        }
        return graphRuntime.getGraphRuntimeState(
            context.measuringPoint,
            undefined,
            bindingGraphSettings && { ...bindingGraphSettings, ...graphTimeWindow }
        );
    }

    function observeBinding<TServiceId extends RuntimeDataServiceId>(
        binding: ActiveDataBinding<TServiceId>,
        resolveContext: RuntimeBindingResolveContext = {}
    ) {
        const location = getCurrentElementLocation();
        const elementPath = location
            ? formatStructurePath(['structure', ...location.elementPath])
            : '<runtime>';
        const bindingContext = { ...runtimeGraphResolveContext, ...resolveContext };

        return dataServices.observeBinding(
            binding,
            {
                elementPath,
            },
            {
                ...bindingContext,
                getDataSetContext: () => getBindingGraphRuntimeState(bindingContext),
            }
        );
    }

    function createElementFromDefinition(definition: ElementDefinition): ReportElement {
        const ElementClass = getElementClassForType(definition.type, elementClassRegistry);
        return new ElementClass(definition.settings as never);
    }

    function getElementTypesForContextKind(contextKind: RenderContextKind) {
        return Array.from(elementClassRegistry.values())
            .filter((ElementClass) => ElementClass.compatibleContextKinds.includes(contextKind))
            .map((ElementClass) => ElementClass.elementType);
    }

    function getInsertionOptionsForSlot(
        contextKind: RenderContextKind,
        slotKind: ElementInsertionSlotKind
    ) {
        return getElementInsertionOptionsForSlotFromRegistry(
            contextKind,
            slotKind,
            elementClassRegistry
        );
    }

    const sharedRuntimeBase: Omit<
        CoreRenderRuntime,
        'getPreviewRuntime' | 'getTemplateIncludeRuntime'
    > = {
        renderSurface,
        profile,
        getOrCreatePageProfile: (pageProfile) =>
            pageProfileRuntime.getOrCreatePageProfile(pageProfile),
        getPageProfiles: () => pageProfileRuntime.getPageProfiles(),
        withElementLocation,
        getCurrentElementLocation,
        getCurrentElementChildListPath,
        getCurrentElementChildListItemPath,
        getElementTypesForContextKind,
        getElementInsertionOptionsForSlot: getInsertionOptionsForSlot,
        observeBinding,
        getGraphRuntimeState: (measuringPoint, elementGraphSettings) =>
            graphRuntime.getGraphRuntimeState(measuringPoint, elementGraphSettings),
        registerRenderedGraph: (measuringPoint, graph, elementGraphSettings) =>
            graphRuntime.registerRenderedGraph(measuringPoint, graph, elementGraphSettings),
        finalizeGraphAutoScaling: () => graphRuntime.finalizeGraphAutoScaling(),
        getRenderedGraphs: () => graphRuntime.getRenderedGraphs(),
        createElementFromDefinition,
        containsFlowableElement: (definitions) =>
            containsFlowableElement(definitions, elementClassRegistry),
    };

    function createPreviewRuntimeForIncludeChain(
        includeChain: readonly string[]
    ): PreviewRenderRuntime {
        const previewRuntime = {} as PreviewRenderRuntime;
        Object.assign(previewRuntime, {
            ...sharedRuntimeBase,
            getPreviewRuntime: () => previewRuntime,
            getTemplateIncludeRuntime: (templateId: string) =>
                createPreviewRuntimeForIncludeChain(
                    appendTemplateIncludeChainOrThrow(includeChain, templateId)
                ),
            showEditor: false,
        });

        return previewRuntime;
    }

    function getPreviewRuntime(): PreviewRenderRuntime {
        return createPreviewRuntimeForIncludeChain([]);
    }

    function getTemplateIncludeRuntime(templateId: string): PreviewRenderRuntime {
        return createPreviewRuntimeForIncludeChain(
            appendTemplateIncludeChainOrThrow([], templateId)
        );
    }

    if (!showEditor) {
        if (renderSurface === 'editor') {
            throw new Error('Preview runtime cannot be created with renderSurface="editor".');
        }
        return getPreviewRuntime();
    }

    if (renderSurface !== 'editor') {
        throw new Error('Editor runtime must be created with renderSurface="editor".');
    }

    if (!structureController) {
        throw new Error('Missing report structure controller for editor runtime.');
    }

    const coreRuntime: CoreRenderRuntime = {
        ...sharedRuntimeBase,
        getPreviewRuntime,
        getTemplateIncludeRuntime,
    };

    return {
        ...coreRuntime,
        showEditor: true,
        editor: {
            structureController,
        },
    };
}
